import { Action } from 'redux'

import { FeatureState } from '@etrigan/feature-toggles'
import { Features } from 'common/features'

/**
 * Redux state for feature toggling in the west
 */
export interface TogglesState {
    featureState: { [name in Features]?: { enabled: boolean } }
    clientOverridable: { [name in Features]?: boolean }
    userOverrides: { [name in Features]?: boolean }
}

/**
 * This action does a shallow merge with existing toggles. This is to ensure that toggles are not lost in error.
 * See: https://jira.swmdigital.io/browse/DPT-127
 */
export const LOAD_FEATURE_TOGGLES = 'toggles/LOAD_FEATURE_TOGGLES'
export interface LOAD_FEATURE_TOGGLES extends Action {
    type: 'toggles/LOAD_FEATURE_TOGGLES'
    payload: {
        features: FeatureState
        overridable?: string[]
    }
}
export const LOAD_USER_TOGGLE_OVERRIDES = 'toggles/LOAD_USER_TOGGLE_OVERRIDES'
export interface LOAD_USER_TOGGLE_OVERRIDES extends Action {
    type: 'toggles/LOAD_USER_TOGGLE_OVERRIDES'
    payload: TogglesState['userOverrides']
}

export const OVERRIDE_FEATURE = 'toggles/OVERRIDE_FEATURE'
export interface OVERRIDE_FEATURE extends Action {
    type: 'toggles/OVERRIDE_FEATURE'
    payload: {
        feature: Features
        enabled: boolean
    }
}

export type FeatureActions = LOAD_FEATURE_TOGGLES | LOAD_USER_TOGGLE_OVERRIDES | OVERRIDE_FEATURE

export const loadFeatures = (
    features: FeatureState,
    overridable?: Features[]
): LOAD_FEATURE_TOGGLES => ({
    type: LOAD_FEATURE_TOGGLES,
    payload: { features, overridable },
})

export const loadUserOverrides = (overrides: TogglesState['userOverrides']) => ({
    type: LOAD_USER_TOGGLE_OVERRIDES,
    payload: overrides,
})

export const overrideFeatureEnabled = (feature: Features, enabled: boolean): OVERRIDE_FEATURE => ({
    type: OVERRIDE_FEATURE,
    payload: { feature, enabled },
})

export const isfeatureToggleAction = (action: Action): action is FeatureActions => {
    return (
        action.type === LOAD_FEATURE_TOGGLES ||
        action.type === LOAD_USER_TOGGLE_OVERRIDES ||
        action.type === OVERRIDE_FEATURE
    )
}

const defaultState = {
    featureState: {},
    clientOverridable: {},
    userOverrides: {},
}

const toggles = (state: TogglesState = defaultState, action: Action): TogglesState => {
    if (!isfeatureToggleAction(action)) {
        return state
    }
    switch (action.type) {
        /**
         * This action does a shallow merge with existing toggles. This is to ensure that toggles are not lost in error.
         * See: https://jira.swmdigital.io/browse/DPT-127
         */
        case LOAD_FEATURE_TOGGLES: {
            const newState: TogglesState = {
                ...state,
                // We need the cast here because we are going from
                // the server toggles which are untyped to a
                // typed toggle state
                featureState: {
                    ...(state.featureState as any),
                    ...(action.payload.features as any),
                },
            }

            if (action.payload.overridable) {
                const overridable = action.payload.overridable.reduce<
                    TogglesState['clientOverridable']
                >((acc, val) => {
                    acc[val as Features] = true
                    return acc
                }, {})
                newState.clientOverridable = overridable
            }
            return newState
        }
        case LOAD_USER_TOGGLE_OVERRIDES:
            return {
                ...state,
                userOverrides: action.payload,
            }
        case OVERRIDE_FEATURE:
            return {
                ...state,
                userOverrides: {
                    ...(state.userOverrides as any),
                    [action.payload.feature]: action.payload.enabled,
                },
            }
    }
}
export default toggles
